import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Copy, FileDown, Link2, FileSpreadsheet, Loader2, Maximize2, X } from 'lucide-react';
import { AudienceLevel, Cohort, LEVEL_LABELS } from '../../types/pathway';
import { PathwayButton } from './PathwayButton';
import { PathwayDocument, pageCount } from './PathwayDocument';
import { DocumentViewer } from './DocumentViewer';
import { PrintSurface, printPathwayDocument } from './PrintSurface';
import { buildPathwayCsv, csvFileName, downloadCsv } from '../../utils/pathwayCsv';
interface ShareModalProps {
  open: boolean;
  pathwayName: string;
  /** Set in the pathway panel; editable here as a last check before sending. */
  company: string;
  items: string[];
  audience: AudienceLevel | 'any';
  cohort: Cohort | null;
  onCompanyChange: (company: string) => void;
  onClose: () => void;
}
const CORE_FIELDS = ['Description', 'Relevance', 'What you’ll learn'];
const EXTRA_FIELDS = ['Category', 'Topics', 'Skill Module ID'];
export function ShareModal({
  open,
  pathwayName,
  company,
  items,
  audience,
  cohort,
  onCompanyChange,
  onClose
}: ShareModalProps) {
  const [extras, setExtras] = useState<string[]>([]);
  const [showExtras, setShowExtras] = useState(false);
  const [status, setStatus] = useState<'idle' | 'working' | 'linked'>('idle');
  const [copied, setCopied] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  if (!open) return null;
  const pageEstimate = pageCount(items.length);
  // Keep the "included" list in the dialog's own order, not click order.
  const selectedExtras = EXTRA_FIELDS.filter(field => extras.includes(field));
  const link = `https://phoenix.edu/pd/pathway/${(pathwayName || 'pathway').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}-8f2c`;
  const toggleExtra = (field: string) => setExtras(prev => prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]);
  const createLink = () => {
    setStatus('working');
    window.setTimeout(() => setStatus('linked'), 700);
  };
  const copyLink = () => {
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  const handleDownloadCsv = () => {
    downloadCsv(csvFileName(pathwayName), buildPathwayCsv({
      pathwayName,
      company,
      cohort,
      audience,
      items,
      fields: selectedExtras
    }));
  };
  return <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4" role="dialog" aria-modal="true" aria-labelledby="share-title" data-id="element-1837" data-mp="27pswfnwls4by">
      <motion.div initial={{
        opacity: 0,
        y: 12,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-lg" data-id="element-1838" data-mp="2hfm8eq0nqlgq">
        <div className="flex items-start justify-between border-b border-gray-200 p-6" data-id="element-1839" data-mp="1hdpizvs0hxxg">
          <div data-id="element-1840" data-mp="1cfe95l1pvnf8">
            <h2 id="share-title" className="text-xl font-bold text-gray-900" data-id="element-1841" data-mp="1cgz1r54h0wag">
              Review and send
            </h2>
            <p className="mt-1 text-sm text-gray-600" data-id="element-1842" data-mp="17intgyttqtuc">
              {items.length} modules ·{' '}
              {audience === 'any' ? 'Mixed audience' : LEVEL_LABELS[audience]}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900" data-id="element-1843" data-mp="yhpnwc3qme9n">
            <X size={18} data-id="element-1844" data-mp="jr8k4m3mrc9l" />
          </button>
        </div>

        <div className="grid flex-1 gap-6 overflow-y-auto p-6 lg:grid-cols-2" data-id="element-1845" data-mp="jstf2ctuwx7z">
          <div className="space-y-5" data-id="element-1846" data-mp="eqm33n911h5n">
            <label className="block" data-id="element-1847" data-mp="etqf36sqhh6v">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1848" data-mp="aq25yjogedji">
                Company
              </span>
              <input value={company} onChange={e => onCompanyChange(e.target.value)} placeholder="Meridian Health" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1849" data-mp="zjapprkodppv" />
              <span className="mt-1 block text-xs text-gray-500" data-id="element-1850" data-mp="gpozt9ksoync">
                Appears on the cover of the PDF and the shared page.
              </span>
            </label>

            <div data-id="element-1851" data-mp="7qae0nfkpc96">
              <span className="block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1852" data-mp="7qtqvpqd9agc">
                Included for every module
              </span>
              <ul className="mt-2 space-y-1.5" data-id="element-1853" data-mp="2242vp47yw7s9">
                {[...CORE_FIELDS, ...selectedExtras].map(field => <li key={field} className="flex items-center gap-2 text-sm text-gray-700" data-id="element-1854" data-mp="265oh5dykhue2">
                    <span className="flex h-4 w-4 items-center justify-center rounded bg-[#007558]" data-id="element-1855" data-mp="3qlco2tm88gr7">
                      <Check size={11} className="text-white" data-id="element-1856" data-mp="3mk0xolvj3rhy" />
                    </span>
                    {field}
                  </li>)}
              </ul>
              <p className="mt-2 text-xs text-gray-500" data-id="element-1857" data-mp="3vkz1b3221x00">
                The first three answer what a prospect actually asks. Anything
                you add here appears in both the PDF and the CSV.
              </p>

              <button type="button" onClick={() => setShowExtras(!showExtras)} aria-expanded={showExtras} className="mt-3 flex items-center gap-1 text-sm font-medium text-[#30718D] transition-colors hover:text-[#265a70]" data-id="element-1858" data-mp="99aav7vaykrp">
                More fields
                <ChevronDown size={14} className={`transition-transform duration-150 ${showExtras ? 'rotate-180' : ''}`} data-id="element-1859" data-mp="2jzikt5okvwph" />
              </button>

              <AnimatePresence initial={false} data-id="element-1860" data-mp="svh0rh4x7yql">
                {showExtras && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.18,
                  ease: [0.23, 1, 0.32, 1]
                }} className="overflow-hidden" data-id="element-1861" data-mp="xsyyschegtjt">
                    <div className="mt-3 space-y-2" data-id="element-1862" data-mp="1brgj0083jnwz">
                      {EXTRA_FIELDS.map(field => <label key={field} className="flex items-center gap-2 text-sm text-gray-700" data-id="element-1863" data-mp="1ljml42s2blpw">
                          <input type="checkbox" checked={extras.includes(field)} onChange={() => toggleExtra(field)} className="h-4 w-4 rounded border-gray-300 text-[#DB3725] focus:ring-[#30718D]" data-id="element-1864" data-mp="1pj4vw5q66gf0" />
                          {field}
                        </label>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-gray-50 p-5" data-id="element-1865" data-mp="1mdvoyqkx0s1">
            <div className="flex items-center justify-between" data-id="element-1866" data-mp="3tqpurcbeuret">
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1867" data-mp="3ttlqqv3p760m">
                Document preview
              </span>
              <button type="button" onClick={() => setViewerOpen(true)} className="flex items-center gap-1 text-sm font-medium text-[#DB3725] transition-colors hover:text-[#c02f1f]" data-id="element-1868" data-mp="3k0dazmbf729q">
                <Maximize2 size={14} data-id="element-1869" data-mp="giv35evndv9b" />
                View full document
              </button>
            </div>

            <button type="button" onClick={() => setViewerOpen(true)} aria-label="Open full document preview" className="mt-3 flex w-full justify-center rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1870" data-mp="3pt90635n5mc2">
              <div className="h-[412px] w-[318px] overflow-hidden rounded-lg border border-gray-200 bg-white" data-id="element-1871" data-mp="3g137er7kf0vt">
                <div className="w-[816px] origin-top-left scale-[0.39]" data-id="element-1872" data-mp="cmpgmhqcwk3h">
                  <PathwayDocument pathwayName={pathwayName} company={company} cohort={cohort} audience={audience} items={items} fields={selectedExtras} coverOnly data-id="element-1873" data-mp="ckmons5n8gw4" />
                </div>
              </div>
            </button>

            <p className="mt-3 text-xs text-gray-500" data-id="element-1874" data-mp="2ngbnh89j1d1b">
              {pageEstimate} {pageEstimate === 1 ? 'page' : 'pages'} · cover
              plus the modules in order.
            </p>
          </div>
        </div>

        <div className="border-t border-gray-200 p-6" data-id="element-1875" data-mp="2ay91dotk5rd5">
          <AnimatePresence initial={false} data-id="element-1876" data-mp="2psdyodz41frx">
            {status === 'linked' && <motion.div initial={{
              opacity: 0,
              y: -6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.2,
              ease: [0.23, 1, 0.32, 1]
            }} className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3" data-id="element-1877" data-mp="2ku2n9yt62x7l">
                <Link2 size={16} className="text-[#30718D]" data-id="element-1878" data-mp="26vcn2ab1whf0" />
                <code className="flex-1 truncate text-sm text-gray-700" data-id="element-1879" data-mp="223b64b2xu2in">
                  {link}
                </code>
                <PathwayButton variant="secondary" size="sm" onClick={copyLink} data-id="element-1880" data-mp="1ed8gs2qeranq">
                  {copied ? <Check size={15} data-id="element-1881" data-mp="19fyu2uueld3l" /> : <Copy size={15} data-id="element-1882" data-mp="19edwsxgpfg21" />}
                  {copied ? 'Copied' : 'Copy'}
                </PathwayButton>
                <span className="w-full text-xs text-gray-500" data-id="element-1883" data-mp="17d76wkg8c9dq">
                  Anyone with the link can view it. You’ll see when the prospect
                  opens it.
                </span>
              </motion.div>}
          </AnimatePresence>

          <div className="flex flex-wrap items-center justify-end gap-3" data-id="element-1884" data-mp="tf0l42ok1zuf">
            <PathwayButton variant="secondary" onClick={onClose} data-id="element-1885" data-mp="oew142ofumzg">
              Cancel
            </PathwayButton>
            <PathwayButton variant="secondary" onClick={createLink} disabled={status === 'working'} className="min-w-[200px]" data-id="element-1886" data-mp="jo5uyn86ug3p">
              {status === 'working' ? <Loader2 size={18} className="animate-spin" data-id="element-1887" data-mp="apkgb35u7l87" /> : <Link2 size={18} data-id="element-1888" data-mp="5nbl005hs72x" />}
              {status === 'linked' ? 'Regenerate Link' : 'Create Shareable Link'}
            </PathwayButton>
            <PathwayButton variant="secondary" onClick={handleDownloadCsv} className="min-w-0 px-5" data-id="element-1889" data-mp="3mr9b0t2u3of8">
              <FileSpreadsheet size={18} data-id="element-1890" data-mp="3ro96ka2258s3" />
              CSV
            </PathwayButton>
            <PathwayButton onClick={() => printPathwayDocument(pathwayName)} data-id="element-1891" data-mp="kc4uyyrg6e4">
              <FileDown size={18} data-id="element-1892" data-mp="5hugix45wzpt" />
              Save as PDF
            </PathwayButton>
          </div>
        </div>
      </motion.div>
    </div>

    <DocumentViewer open={viewerOpen} pathwayName={pathwayName} company={company} cohort={cohort} audience={audience} items={items} fields={selectedExtras} onClose={() => setViewerOpen(false)} onDownloadCsv={handleDownloadCsv} data-id="element-1893" data-mp="33yewu1d1c0c2" />

    <PrintSurface pathwayName={pathwayName} company={company} cohort={cohort} audience={audience} items={items} fields={selectedExtras} data-id="element-1894" data-mp="3iqqj6z0xyid1" />
    </>;
}